import { createStore } from 'zustand/vanilla';
import type { TutorialStoreState, TutorialProgress } from '../types';

/**
 * Factory that creates a namespaced tutorial progress store.
 * Persists all progress to localStorage.
 */
export function createTutorialStore(productId: string) {
  const storageKey = `${productId}-tutorials`;

  function loadProgress(): Record<string, TutorialProgress> {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return {};
      return JSON.parse(raw) as Record<string, TutorialProgress>;
    } catch {
      return {};
    }
  }

  function persistProgress(progress: Record<string, TutorialProgress>) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(progress));
    } catch {
      // silently skip
    }
  }

  const initial = loadProgress();

  return createStore<TutorialStoreState>()((set, get) => ({
    activeTutorialId: null,
    progress: initial,

    startTutorial: (id: string, _totalSteps: number) => {
      const existing = get().progress[id];
      const entry: TutorialProgress = existing && !existing.completed
        ? { ...existing, dismissed: false, lastActivity: new Date().toISOString() }
        : {
            tutorialId: id,
            currentStep: 0,
            completed: false,
            dismissed: false,
            lastActivity: new Date().toISOString(),
          };

      const progress = { ...get().progress, [id]: entry };
      set({ activeTutorialId: id, progress });
      persistProgress(progress);
    },

    nextStep: () => {
      const { activeTutorialId, progress } = get();
      if (!activeTutorialId) return;

      const entry = progress[activeTutorialId];
      if (!entry) return;

      const next: TutorialProgress = {
        ...entry,
        currentStep: entry.currentStep + 1,
        lastActivity: new Date().toISOString(),
      };
      const updated = { ...progress, [activeTutorialId]: next };
      set({ progress: updated });
      persistProgress(updated);
    },

    prevStep: () => {
      const { activeTutorialId, progress } = get();
      if (!activeTutorialId) return;

      const entry = progress[activeTutorialId];
      if (!entry || entry.currentStep <= 0) return;

      const next: TutorialProgress = {
        ...entry,
        currentStep: entry.currentStep - 1,
        lastActivity: new Date().toISOString(),
      };
      const updated = { ...progress, [activeTutorialId]: next };
      set({ progress: updated });
      persistProgress(updated);
    },

    skipTutorial: () => {
      const { activeTutorialId, progress } = get();
      if (!activeTutorialId) return;

      const entry = progress[activeTutorialId];
      if (!entry) return;

      const next: TutorialProgress = {
        ...entry,
        dismissed: true,
        lastActivity: new Date().toISOString(),
      };
      const updated = { ...progress, [activeTutorialId]: next };
      set({ activeTutorialId: null, progress: updated });
      persistProgress(updated);
    },

    completeTutorial: () => {
      const { activeTutorialId, progress } = get();
      if (!activeTutorialId) return;

      const entry = progress[activeTutorialId];
      if (!entry) return;

      const next: TutorialProgress = {
        ...entry,
        completed: true,
        lastActivity: new Date().toISOString(),
      };
      const updated = { ...progress, [activeTutorialId]: next };
      set({ activeTutorialId: null, progress: updated });
      persistProgress(updated);
    },

    resetTutorial: (id: string) => {
      const { progress, activeTutorialId } = get();
      const { [id]: _, ...rest } = progress;
      set({
        progress: rest,
        activeTutorialId: activeTutorialId === id ? null : activeTutorialId,
      });
      persistProgress(rest);
    },
  }));
}

export type TutorialStore = ReturnType<typeof createTutorialStore>;
